import { injectable, inject } from "inversify";
import {
    ErrorCode,
    ErrorSeverity,
    FlexSdkError,
    InternalError,
    ThrowErrorFromResponseFunction,
    throwErrorFromResponseRTTI,
    ThrowErrorFunction,
    throwErrorRTTI
} from "~/modules/error";
import { sessionRTTI, Session } from "~/modules/session";
import { HttpAdapter } from "./HttpAdapter";
import { EnvironmentConfig, environmentConfigRTTI } from "~/modules/config";
import { mapRootKeysToCamelCase, mapRootKeysToSnakeCase } from "~/utils/mapKeys";
import { extractFileNameFromPath } from "~/utils/extractFromPath";
import { retry } from "~/utils/retry/retry";
import { defaultRetryConditionOnFlexSdkError } from "~/utils/defaultRetryConditionOnFlexSdkError";
import { AuthenticationMethod, makeAuthenticationHeaders } from "./authenticationMethods";

@injectable()
export class HttpAdapterImpl implements HttpAdapter {
    private readonly session: Session;

    private readonly envConfig: EnvironmentConfig;

    readonly #throwError: ThrowErrorFunction;

    readonly #throwErrorFromResponse: ThrowErrorFromResponseFunction;

    constructor(
        @inject(sessionRTTI) session: Session,
        @inject(environmentConfigRTTI) envConfig: EnvironmentConfig,
        @inject(throwErrorRTTI) throwError: ThrowErrorFunction,
        @inject(throwErrorFromResponseRTTI) throwErrorFromResponse: ThrowErrorFromResponseFunction
    ) {
        this.session = session;
        this.envConfig = envConfig;
        this.#throwError = throwError;
        this.#throwErrorFromResponse = throwErrorFromResponse;
    }

    public get<T>(url: string, authMethod?: AuthenticationMethod, options?: { [key: string]: unknown }): Promise<T> {
        return this.performNetworkCall<T>(url, "GET", authMethod, undefined, options);
    }

    public post<T>(
        url: string,
        authMethod?: AuthenticationMethod,
        body?: {},
        options?: { [key: string]: unknown }
    ): Promise<T> {
        return this.performNetworkCall<T>(url, "POST", authMethod, body, options);
    }

    public put<T>(url: string, authMethod?: AuthenticationMethod, body?: {}): Promise<T> {
        return this.performNetworkCall<T>(url, "PUT", authMethod, body);
    }

    public delete<T>(url: string, authMethod?: AuthenticationMethod): Promise<T> {
        return this.performNetworkCall<T>(url, "DELETE", authMethod);
    }

    private getRequestData(
        method: string,
        authMethod?: AuthenticationMethod,
        body?: object,
        options?: { [key: string]: unknown }
    ): { headers: Headers | undefined; method: string; body: string } {
        let headers;

        if (authMethod) {
            headers = makeAuthenticationHeaders(authMethod, this.#getToken(options?.token));
        } else {
            headers = new Headers({
                "Content-Type": "application/json"
            });
        }

        return { headers, method, body: JSON.stringify(body) };
    }

    private getEnvironmentSpecificUrl(url: string): string {
        let urlEnvSpecifier = "";
        const region = this.envConfig.region || "";
        if (region.startsWith("dev-")) {
            urlEnvSpecifier = ".dev";
        } else if (region.startsWith("stage-")) {
            urlEnvSpecifier = ".stage";
        }
        return url.replace("[environment]", urlEnvSpecifier);
    }

    private mapBody(body: object | undefined): { [key: string]: unknown } | undefined {
        if (body) {
            return mapRootKeysToSnakeCase(body);
        }
        return body;
    }

    #mapStatusCodeToFlexSdkErrorCode = (statusCode: number): ErrorCode | undefined => {
        const HTTPS_STATUS_CODE_TOO_MANY_REQUESTS = 429;
        const HTTPS_STATUS_CODE_INTERNAL_SERVER_ERROR = 500;

        let errorCode;
        if (statusCode === HTTPS_STATUS_CODE_TOO_MANY_REQUESTS) {
            errorCode = ErrorCode.TooManyRequests;
        } else if (statusCode >= HTTPS_STATUS_CODE_INTERNAL_SERVER_ERROR) {
            errorCode = ErrorCode.Unknown;
        }
        return errorCode;
    };

    #handleErrorResponse = async (response: Response): Promise<void> => {
        const metadata = {
            module: "backend",
            severity: ErrorSeverity.Error,
            source: extractFileNameFromPath(__filename)
        };
        const flexSdkErrorCode = this.#mapStatusCodeToFlexSdkErrorCode(response.status);

        if (flexSdkErrorCode) {
            this.#throwError(flexSdkErrorCode, metadata);
        }
        await this.#throwErrorFromResponse(response, metadata);
    };

    private async performNetworkCall<T>(
        url: string,
        method: string,
        authMethod?: AuthenticationMethod,
        body?: object,
        options?: { [key: string]: unknown }
    ): Promise<T> {
        return retry({
            functionToRetry: () => this.#performNetworkCallOnce<T>(url, method, authMethod, body, options),
            retryCondition: defaultRetryConditionOnFlexSdkError
        });
    }

    #performNetworkCallOnce = async <T>(
        url: string,
        method: string,
        authMethod?: AuthenticationMethod,
        body?: object,
        options?: { [key: string]: unknown }
    ): Promise<T> => {
        const environmentSpecificUrl = this.getEnvironmentSpecificUrl(url);
        let response: Response;

        try {
            response = await fetch(
                environmentSpecificUrl,
                this.getRequestData(method, authMethod, this.mapBody(body), options)
            );
        } catch (e) {
            throw new FlexSdkError(ErrorCode.NetworkError, undefined, undefined, e);
        }

        if (!response.ok) {
            await this.#handleErrorResponse(response);
        }
        const result = await response.json();

        return mapRootKeysToCamelCase(result) as Promise<T>;
    };

    #getToken = (token?: unknown) => {
        if (!token) {
            return this.session.token;
        }

        if (typeof token === "string") {
            return token;
        }

        throw new InternalError("No token in request body");
    };
}
